/**
 * Security Service
 * 
 * Provides access to security-related APIs including login history,
 * security stats, alerts, and session management.
 */

export interface LoginRecord {
  id: number
  login_type: 'success' | 'failed' | 'blocked' | 'logout'
  login_method: string
  ip_address: string
  location: string
  country?: string
  city?: string
  device: string
  device_type?: string
  device_brand?: string
  os_name?: string
  os_version?: string
  browser_name?: string
  browser_version?: string
  risk_score: number
  risk_level: 'low' | 'medium' | 'high'
  is_suspicious: boolean
  security_flags: string[]
  is_new_device: boolean
  is_new_location: boolean
  is_new_browser: boolean
  two_factor_used: boolean
  two_factor_method?: string
  failed_attempts_before: number
  session_duration?: string
  is_active: boolean
  session_terminated: boolean
  termination_reason?: string
  created_at: string
  error_code?: string
  error_message?: string
}

export interface SecurityAlert extends LoginRecord {
  alert_types: string[]
  severity: 'medium' | 'high'
}

export interface SecurityStats {
  success: boolean
  period_days: number
  total_logins: number
  successful_logins: number
  failed_logins: number
  success_rate: number
  unique_ips: number
  unique_devices: number
  unique_locations: number
  high_risk_logins: number
  suspicious_logins: number
  new_devices: number
  new_locations: number
  mfa_used: number
  mfa_rate: number
  login_methods: Record<string, number>
  device_types: Record<string, number>
  browsers: Record<string, number>
  operating_systems: Record<string, number>
  locations: Array<{ location: string; count: number }>
  risk_distribution: { low: number; medium: number; high: number }
  login_timeline: Array<{
    date: string
    success: number
    failed: number
    total: number
  }>
}

export interface SecurityDashboard {
  success: boolean
  stats: SecurityStats
  alerts: SecurityAlert[]
  sessions: LoginRecord[]
  user: {
    id: number
    username: string
    email: string
    mfa_enabled: boolean
    email_verified: boolean
    last_login_at?: string
    last_login_ip?: string
    oauth_provider?: string
    has_local_password?: boolean
  }
}

class SecurityService {
  private baseUrl = '/api/security'

  /**
   * Get comprehensive security dashboard data
   */
  async getDashboard(days: number = 30): Promise<SecurityDashboard> {
    const response = await fetch(`${this.baseUrl}/dashboard?days=${days}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get security dashboard')
    }

    return response.json()
  }

  /**
   * Get login history with pagination
   */
  async getLoginHistory(options?: {
    limit?: number
    offset?: number
    days?: number
  }): Promise<{
    success: boolean
    logins: LoginRecord[]
    total: number
    limit: number
    offset: number
    has_more: boolean
  }> {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', options.limit.toString())
    if (options?.offset) params.set('offset', options.offset.toString())
    if (options?.days) params.set('days', options.days.toString())

    const url = `${this.baseUrl}/login-history${params.toString() ? '?' + params.toString() : ''}`
    
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get login history')
    }

    return response.json()
  }

  /**
   * Get security statistics
   */
  async getStats(days: number = 30): Promise<SecurityStats> {
    const response = await fetch(`${this.baseUrl}/stats?days=${days}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get security stats')
    }

    return response.json()
  }

  /**
   * Get security alerts
   */
  async getAlerts(days: number = 7): Promise<{
    success: boolean
    alerts: SecurityAlert[]
    total: number
    period_days: number
  }> {
    const response = await fetch(`${this.baseUrl}/alerts?days=${days}`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get security alerts')
    }

    return response.json()
  }

  /**
   * Get active sessions
   */
  async getActiveSessions(): Promise<{
    success: boolean
    sessions: LoginRecord[]
    total: number
  }> {
    const response = await fetch(`${this.baseUrl}/sessions`, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to get active sessions')
    }

    return response.json()
  }

  /**
   * Terminate a specific session
   */
  async terminateSession(sessionId: number): Promise<{
    success: boolean
    message: string
  }> {
    const response = await fetch(`${this.baseUrl}/sessions/${sessionId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to terminate session')
    }

    return response.json()
  }

  /**
   * Terminate all sessions
   */
  async terminateAllSessions(exceptCurrent: boolean = true): Promise<{
    success: boolean
    message: string
    terminated_count: number
  }> {
    const response = await fetch(`${this.baseUrl}/sessions/terminate-all`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ except_current: exceptCurrent })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to terminate sessions')
    }

    return response.json()
  }

  /**
   * Get risk level color class
   */
  getRiskColorClass(riskLevel: string): string {
    switch (riskLevel) {
      case 'high':
        return 'text-bad'
      case 'medium':
        return 'text-warn'
      case 'low':
      default:
        return 'text-ok'
    }
  }

  /**
   * Get risk level background class
   */
  getRiskBgClass(riskLevel: string): string {
    switch (riskLevel) {
      case 'high':
        return 'bg-bad'
      case 'medium':
        return 'bg-warn'
      case 'low':
      default:
        return 'bg-ok'
    }
  }
}

// Export singleton instance
export const securityService = new SecurityService()

